import logger from "../log";

import iOS from "./detection";

/**
 * Hand the recap card to the best share sheet available.
 *
 * Inside the Volley apps the page runs in a web view, and the apps expose a
 * bridge that opens the native sheet with the card itself. Builds without it
 * fall back to the page's own Web Share, and where there's no share sheet at
 * all (desktop browsers, an older Android app) the player saves the card.
 */

export interface SharePayload {
    /** The card as it's previewed: the stats alone, or on the player's photo. */
    image: Blob;
    text: string;
    /** Name for the image when it's shared or saved as a file. */
    fileName: string;
}

export type ShareSurface = "ios" | "rn" | "web_files";

export type ShareOutcome =
    | { status: "shared"; surface: ShareSurface }
    /** Handed to the app's share sheet, which takes it from there. */
    | { status: "handed_off"; surface: ShareSurface }
    | { status: "cancelled"; surface: ShareSurface }
    /** The browser wants a fresh tap before it will open the sheet. */
    | { status: "needs_gesture" }
    /** The share sheet failed for a reason a retry won't fix. */
    | { status: "failed"; reason: string }
    /** No share sheet here: the player saves the card and posts it. */
    | { status: "no_share_sheet" };

interface IosShareBridge {
    /**
     * A reply handler: resolves once the app accepts the share and rejects
     * with its reason when it refuses one (a page or payload it won't trust).
     */
    postMessage(message: unknown): Promise<unknown> | undefined;
}

/** What an app build announces it can do, injected before the page loads. */
interface ShareCapabilities {
    /** The photo picker is safe here (the app can use the camera). */
    customPhoto?: boolean;
    /**
     * The app's bridge takes the card itself. Earlier builds of either app
     * have no bridge, or one that only knew links.
     */
    shareImage?: boolean;
}

declare global {
    interface Window {
        webkit?: { messageHandlers?: { volleyShare?: IosShareBridge } };
        ReactNativeWebView?: { postMessage(message: string): void };
        volleyShareCapabilities?: ShareCapabilities;
    }
}

/** The apps refuse anything longer, so a bigger card isn't sent. */
const MAX_BRIDGE_IMAGE_CHARS = 4_000_000;

function isAbort(e: unknown): boolean {
    return e instanceof DOMException && e.name === "AbortError";
}

function isNotAllowed(e: unknown): boolean {
    return e instanceof DOMException && e.name === "NotAllowedError";
}

/** Save the card as a file. Call from a tap: browsers block it otherwise. */
export function downloadCard(image: Blob, fileName: string): void {
    const href = URL.createObjectURL(image);
    const a = document.createElement("a");
    a.href = href;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(href), 10_000);
}

/**
 * Whether the page is inside one of the Volley apps, old builds included:
 * every iOS app build registers a message handler on its web view, and the
 * Android app replaces the web view's user agent with its own. An iOS web
 * view with no "Safari" in its user agent counts too, since another app's
 * in-app browser can't be told from an app build that predates both.
 */
function inVolleyApp(): boolean {
    if (window.webkit?.messageHandlers || window.ReactNativeWebView) {
        return true;
    }
    const ua = navigator.userAgent;
    return ua.includes("GetVolley") || (iOS() && !ua.includes("Safari/"));
}

/**
 * Whether a recap can be shared from here: any browser, or an app build that
 * takes the card itself. Older builds have nowhere to send it (the Android
 * app can't even save it), so they don't offer a recap at all.
 */
export function canShareRecap(): boolean {
    return (
        !inVolleyApp() || window.volleyShareCapabilities?.shareImage === true
    );
}

/**
 * Whether this page can offer the "Your photo" card.
 *
 * Inside the Volley apps only when the build says so: without a camera
 * usage description the picker's "Take Photo" crashes the iOS app.
 */
export function canShareCustomPhoto(): boolean {
    const canDraw =
        typeof createImageBitmap === "function" &&
        typeof HTMLCanvasElement.prototype.toBlob === "function";
    if (window.volleyShareCapabilities?.customPhoto === true) return canDraw;
    return !inVolleyApp() && canDraw;
}

function base64(image: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () =>
            resolve(String(reader.result).replace(/^data:[^,]*,/, ""));
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(image);
    });
}

/** The card for an app's bridge, or null when it's too big to send. */
async function bridgeImage(image: Blob): Promise<string | null> {
    const imageData = await base64(image);
    if (imageData.length <= MAX_BRIDGE_IMAGE_CHARS) return imageData;
    logger.error(
        `Recap card too big for the app bridge: ${imageData.length} chars`,
    );
    return null;
}

/**
 * Call from the tap that asked to share, with the card already in hand:
 * browsers drop the tap's permission to open a share sheet if the page waits
 * too long first.
 */
export async function shareRecap(payload: SharePayload): Promise<ShareOutcome> {
    const ios = window.webkit?.messageHandlers?.volleyShare;
    const rn =
        window.volleyShareCapabilities?.shareImage === true
            ? window.ReactNativeWebView
            : undefined;
    const imageData = ios || rn ? await bridgeImage(payload.image) : null;

    if (ios && imageData) {
        try {
            await ios.postMessage({
                text: payload.text,
                imageData,
                fileName: payload.fileName,
            });
            return { status: "handed_off", surface: "ios" };
        } catch (e) {
            // The app refused it, so no sheet is coming: share from the page.
            logger.error(`iOS share bridge refused the recap: ${String(e)}`);
        }
    }
    if (rn && imageData) {
        // No reply comes back: the app says so itself if it can't share.
        rn.postMessage(
            JSON.stringify({
                type: "volley.share",
                text: payload.text,
                imageData,
                fileName: payload.fileName,
            }),
        );
        return { status: "handed_off", surface: "rn" };
    }

    const file = new File([payload.image], payload.fileName, {
        type: payload.image.type || "image/png",
    });
    const data: ShareData = { files: [file], text: payload.text };
    if (typeof navigator.share === "function" && navigator.canShare?.(data)) {
        return webShare(data);
    }
    return { status: "no_share_sheet" };
}

async function webShare(data: ShareData): Promise<ShareOutcome> {
    const surface = "web_files";
    try {
        await navigator.share(data);
        return { status: "shared", surface };
    } catch (e) {
        if (isAbort(e)) return { status: "cancelled", surface };
        if (isNotAllowed(e)) return { status: "needs_gesture" };
        const reason = e instanceof Error ? e.name : String(e);
        logger.error(`navigator.share failed: ${reason}: ${String(e)}`);
        return { status: "failed", reason };
    }
}
